import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import * as Models from '@usimweb/core/models';
// import { Helper } from '@usimweb/core/services';

import { map, Observable, switchMap } from 'rxjs';
import { saveAs } from 'file-saver';

export const USIM_API = 'api';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private HOST!: string;

  private PROJECT!: string;
  private PROJECT_VERSION!: string;
  private CONTACTS!: string;
  private USER_CREDITS!: string;
  private CONTACT_REQUEST!: string;
  private USIM_DATA!: string;
  private PART_PD!: string;
  private MOLDED_PROJECT!: string;
  private MASTER_MODEL!: string;
  private STONE_CHIP!: string;
  private NEWS!: string;
  private MATERIAL!: string;

  public CONSENT!: string;

  public readonly TYPE_FOAMAT = 'foamat';
  public readonly TYPE_FOAMAT_VISCOSITY = 'foamat-viscosity';
  public readonly TYPE_Density = 'density';
  public readonly TYPE_VISCOSITY = 'viscosity';

  private httpOptions = {
    json: {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    },
    arrayblob: {
      headers: new HttpHeaders({
        'Content-Type': 'arrayblob',
      }),
    },
  };

  constructor(
    private httpclient: HttpClient,
    @Inject('environment') private environment: Models.Env
  ) {
    this.HOST = environment.USIM_HOST;
    if (environment.APP === 'molded') {
      this.PROJECT = environment.USIM_HOST + '/api/project/';
      this.PROJECT_VERSION = environment.USIM_HOST + '/api/project-version/';
    } else {
      this.PROJECT = environment.USIM_HOST + '/api/' + environment.APP + '-project/';
      this.PROJECT_VERSION = environment.USIM_HOST + '/api/' + environment.APP + '-project-version/';
    }

    this.MOLDED_PROJECT = environment.USIM_HOST + '/api/project/';
    this.CONTACTS = environment.USIM_HOST + '/api/usercontacts/';
    this.USER_CREDITS = environment.USIM_HOST + '/api/user_credits/';
    this.CONTACT_REQUEST = environment.USIM_HOST + '/api/contactrequests/';

    this.USIM_DATA = environment.USIM_HOST + '/api/usimdata/';
    this.PART_PD = environment.USIM_HOST + '/api/partpd/';
    this.MASTER_MODEL = environment.USIM_HOST + '/api/mastermodel/';
    this.STONE_CHIP = environment.USIM_HOST + '/api/stonechip/';
    this.NEWS = environment.USIM_HOST + '/api/news/';
    this.MATERIAL = environment.USIM_HOST + '/api/material/';

    this.CONSENT = environment.USIM_HOST + '/api/userconsent/';
  }

  // shared

  public addProject(projects: Models.Projects): Observable<any> {
    return this.httpclient.post(this.PROJECT, projects, this.httpOptions.json);
  }

  public addProjectVersion(version: any): Observable<Models.Versions> {
    return this.httpclient.post<Models.Versions>(this.PROJECT_VERSION, version, this.httpOptions.json);
  }

  public deleteProject(uuid: string): Observable<object> {
    return this.httpclient.delete(this.PROJECT + uuid + '/');
  }

  public requestSupport(uuid: string, reqSupport: {}): Observable<Models.Projects> {
    return this.httpclient.post<Models.Projects>(this.PROJECT + uuid + '/request_support/', reqSupport, this.httpOptions.json);
  }

  public requestUnSupport(requestData: any): Observable<Models.Projects> {
    return this.httpclient.post<Models.Projects>(this.PROJECT + requestData.uuid + '/unsupport/', requestData, this.httpOptions.json);
  }

  public requestAddUserToProject(uuid: string, user: string): Observable<object> {
    const object = { username: user };
    return this.httpclient.post(this.PROJECT + uuid + '/add_user/', object, this.httpOptions.json);
  }

  public requestRemoveUserFromProject(uuid: string, user: string): Observable<object> {
    const object = { username: user };
    return this.httpclient.post(this.PROJECT + uuid + '/remove_user/', object, this.httpOptions.json);
  }

  public requestProjectVisibility(uuid: string, visibility: boolean): Observable<object> {
    const object = { is_public: visibility };
    return this.httpclient.patch(this.PROJECT + uuid + '/change_is_public/', object, this.httpOptions.json);
  }

  public deleteProjectVersion(uuid: string): Observable<object> {
    return this.httpclient.delete(this.PROJECT_VERSION + uuid + '/');
  }

  public renameVersion(uuid: string, name: any): Observable<{ name: string }> {
    const obj = { name: name };
    return this.httpclient.patch<{ name: string }>(this.PROJECT_VERSION + uuid + '/rename/', obj, this.httpOptions.json);
  }

  public getProjects(): Observable<Models.Projects[]> {
    return this.httpclient.get<Models.Page>(this.PROJECT).pipe(map((page: Models.Page) => page.results as Models.Projects[]));
  }

  public getMoldedProjects(): Observable<Models.Projects[]> {
    return this.httpclient.get<Models.Page>(this.MOLDED_PROJECT).pipe(map((page: Models.Page) => page.results as Models.Projects[]));
  }

  public getVersions(uuid: string): Observable<Models.Versions[]> {
    return this.httpclient.get<Models.Projects>(this.PROJECT + uuid + '/').pipe(
      map((projects: Models.Projects) => {
        return projects.versions;
      })
    );
  }

  public getVersion(uuid: string): Observable<Models.Versions> {
    return this.httpclient.get<Models.Versions>(this.PROJECT_VERSION + uuid + '/');
  }

  public getProjectById(uuid: string): Observable<Models.Projects> {
    return this.httpclient.get<Models.Projects>(this.PROJECT + uuid + '/');
  }

  public getGeometriePreview(uuid: string, fileType = 'png'): Observable<any> {
    return this.httpclient.get(this.PROJECT_VERSION + uuid + '/preview/?filetype=' + fileType, {
      responseType: 'blob',
    });
  }

  public getProjectPreview(uuid: string): Observable<any> {
    return this.httpclient.get(this.PROJECT + uuid + '/preview/', {
      responseType: 'blob',
    });
  }

  public getProjectPreviewVersion(uuid: string): Observable<any> {
    return this.httpclient.get(this.PROJECT_VERSION + uuid + '/preview/', { responseType: 'blob' });
  }

  public getContacts(): Observable<Models.Contacts[]> {
    return this.httpclient.get<Models.Contacts[]>(this.CONTACTS);
  }

  public pendingRequests(): Observable<Models.PendingContacts[]> {
    return this.httpclient.get<Models.PendingContacts[]>(this.CONTACT_REQUEST + 'sent_requests/');
  }

  public getProjectComments(uuid: any): Observable<any> {
    return this.httpclient.get<any>(this.PROJECT + uuid + '/chat/');
  }

  public getVersionComments(version_uuid: string): Observable<Models.Comments[]> {
    return this.httpclient.get<Models.Comments[]>(this.PROJECT_VERSION + version_uuid + '/chat/');
  }

  // shift to comments store
  public getComments(project_uuid: string): Observable<Models.Comments[]> {
    return this.getProjectComments(project_uuid).pipe(
      map((comments) => {
        comments.forEach((comment: Models.Comments) => {
          // if (comment.created_by.username.toLowerCase() === AuthService.basfUserName.toLowerCase()) {
          //   comment.editable = true;
          // }
          // comment.lastColor = comment.color;
          // comment.selected = false;
          // comment.replies.forEach((reply) => {
          //   if (reply.created_by.username.toLocaleLowerCase() === AuthService.basfUserName.toLocaleLowerCase()) {
          //     reply.editable = true;
          //   }
          // });
        });
        return comments;
      })
    );
  }

  public addComments(comment: any, project_uuid: string): Observable<Models.Comments[]> {
    return this.addProjectComment(comment, project_uuid).pipe(switchMap(() => this.getComments(project_uuid)));
  }

  public deleteComment(project_uuid: string, comment_uuid: string): Observable<Models.Comments[]> {
    return this.deleteProjectComment(project_uuid, comment_uuid).pipe(switchMap(() => this.getComments(project_uuid)));
  }

  public addReply(project_uuid: string, comment_uuid: string, version_uuid: string, reply: string): Observable<Models.Comments[]> {
    return this.addProjectCommentReply(project_uuid, comment_uuid, version_uuid, reply).pipe(switchMap(() => this.getComments(project_uuid)));
  }

  public updateComment(comment: any, project_uuid: string, comment_uuid: string, version_uuid: string): Observable<Models.Comments[]> {
    return this.updateProjectComment(comment, project_uuid, comment_uuid, version_uuid).pipe(switchMap(() => this.getComments(project_uuid)));
  }

  public updateReply(content: any, project_uuid: string, comment_uuid: string, version_uuid: string): Observable<Models.Comments[]> {
    return this.updateProjectReply(content, project_uuid, comment_uuid, version_uuid).pipe(switchMap(() => this.getComments(project_uuid)));
  }

  public updateFavouriteStatus(uuid: string, is_favorite: boolean) {
    const object = { is_favorite: is_favorite };
    return this.httpclient.patch(this.PROJECT + uuid + '/change_favorite_status/', object, this.httpOptions.json);
  }

  public deleteContact(username: string): Observable<Models.Contacts[]> {
    const data = { user: username };
    return this.httpclient.post<Models.Contacts[]>(this.CONTACTS + 'remove_contact/', data, this.httpOptions.json);
  }

  public sendContactRequest(email: string): Observable<any> {
    const dict = { email: email, app_name: this.environment.APP };
    return this.httpclient.post(this.CONTACT_REQUEST, dict);
  }

  public addContact(emailObject: object): Observable<any> {
    return this.httpclient.post(this.CONTACT_REQUEST, emailObject, this.httpOptions.json);
  }

  public sentRequests(): Observable<any> {
    return this.httpclient.post(this.CONTACT_REQUEST + 'sent_requests/', this.httpOptions.json);
  }

  public receivedRequests(): Observable<Models.PendingContacts[]> {
    return this.httpclient.get<Models.PendingContacts[]>(this.CONTACT_REQUEST + 'requests/', this.httpOptions.json);
  }

  public getContactRequests(): Observable<any> {
    return this.httpclient.get(this.CONTACT_REQUEST + 'requests/', this.httpOptions.json);
  }

  public getProjectContacts(uuid: string): Observable<Models.Contacts[]> {
    return this.httpclient.get<Models.Contacts[]>(this.PROJECT + `${uuid}/contacts/`);
  }

  public getContactRequest(contactRequestId: number): Observable<any> {
    return this.httpclient.get(this.CONTACT_REQUEST + `${contactRequestId}/`, this.httpOptions.json);
  }

  public acceptInvite(inviteId: number): Observable<any> {
    return this.httpclient.post(this.CONTACT_REQUEST + `${inviteId}/accept_invite/`, {}, this.httpOptions.json);
  }

  public rejectInvite(inviteId: number): Observable<any> {
    return this.httpclient.post(this.CONTACT_REQUEST + `${inviteId}/reject_invite/`, {}, this.httpOptions.json);
  }

  public addUserToProject(uuid: string, data: any): Observable<Models.UserSharing> {
    const url = `${this.PROJECT}${uuid}/user/`;
    return this.httpclient.post<Models.UserSharing>(url, data, this.httpOptions.json);
  }

  public removeUserFromProject(uuid: string, data: any): Observable<Models.UserSharing> {
    const url = `${this.PROJECT}${uuid}/remove_user/`;
    return this.httpclient.post<Models.UserSharing>(url, data, this.httpOptions.json);
  }

  public changeUserRole(uuid: string, data: any): Observable<any> {
    const url = `${this.PROJECT}${uuid}/change_role/`;
    return this.httpclient.post<any>(url, data, this.httpOptions.json);
  }

  public getCredits(): Observable<Models.Credits> {
    return this.httpclient.get<Models.Credits>(this.USER_CREDITS, this.httpOptions.json);
  }

  // public getTree() {
  //   return this.httpclient.get<Models.VelaInputTest>(this.PROJECT + 'getStructure/', this.httpOptions.json);
  //   return this.httpclient.get<Models.VelaInput[]>(this.PROJECT + 'templates/', this.httpOptions.json);
  // }

  public getVelaTemplates() {
    return this.httpclient.get<Models.VelaTemplate[]>(this.PROJECT + 'templates/', this.httpOptions.json);
  }

  public getNews(): any {
    return this.httpclient.get<any>(this.NEWS + 'latest/', this.httpOptions.json);
  }

  public toggleNews(uuid: string) {
    return this.httpclient.patch(this.NEWS + uuid + '/toggle_read/', this.httpOptions.json);
  }

  // end

  // foaming

  public getFoamingResult(uuid: string, type: string): Observable<any> {
    return this.httpclient.get<any>(this.PROJECT_VERSION + uuid + '/result/?plotType=' + type);
  }

  public getCeetronToken(uuid: string, accessToken: string): Observable<Models.Token> {
    return this.httpclient.put<Models.Token>(this.PROJECT_VERSION + uuid + '/token/', this.httpOptions.json).pipe(
      map((response: any) => {
        const tokenId = response['id'];
        return {
          access_token: accessToken,
          modelKey: tokenId,
        };
      })
    );
  }

  public getResultCurves(uuid: string, type: string): Observable<any> {
    return this.httpclient.get<any>(this.PROJECT_VERSION + uuid + '/plotly/?plotType=' + type);
  }

  public addProjectComment(comment: any, project_uuid: string): Observable<any> {
    return this.httpclient.post(this.PROJECT + project_uuid + '/chat/', comment, this.httpOptions.json);
  }

  public updateProjectComment(comment: any, project_uuid: string, comment_uuid: string, version_uuid: string): Observable<any> {
    const obj = {
      content: comment.content,
      color: comment.color,
      meta: comment.meta,
      comment_id: comment_uuid,
      tags: ['v_' + version_uuid],
    };
    return this.httpclient.put(this.PROJECT + project_uuid + '/chat/?comment_id=/' + comment_uuid, obj);
  }

  public updateProjectReply(content: any, project_uuid: string, comment_uuid: string, version_uuid: string): Observable<any> {
    const obj = { content: content, comment_id: comment_uuid, tags: ['v_' + version_uuid] };
    return this.httpclient.put(this.PROJECT + project_uuid + '/chat/?comment_id=/' + comment_uuid, obj);
  }

  public deleteProjectComment(project_uuid: string, comment_uuid: string): Observable<any> {
    return this.httpclient.delete<Models.Comments[]>(this.PROJECT + project_uuid + '/chat/?comment_id=' + comment_uuid);
  }

  public addProjectCommentReply(project_uuid: string, comment_uuid: string, version_uuid: string, reply: string) {
    const obj = { content: reply, reply_to: comment_uuid, tags: ['v_' + version_uuid] };
    return this.httpclient.post(this.PROJECT + project_uuid + '/chat/', obj, this.httpOptions.json);
  }

  public getFoamingPpx(uuid: string): Observable<any> {
    return this.httpclient.get(this.PROJECT_VERSION + uuid + '/pptx/');
  }

  public getUsimMaterials(app: string): Observable<Models.Foam[]> {
    return this.httpclient.get<Models.Page>(this.MATERIAL + '?usimtask=' + app).pipe(
      map((result) => {
        let foams = result.results as Models.Foam[];
        return foams;
      })
    );
  }

  public getRTData(slug: string, y2 = true): Observable<any> {
    return this.httpclient.get<Models.Measurement>(this.USIM_DATA + slug + '/RTData').pipe(
      map((response: Models.Measurement) => {
        const curves: any[] = [];

        if (response[this.TYPE_Density] && Object.keys(response[this.TYPE_Density]).length !== 0) {
          curves.push(response[this.TYPE_Density]);
        }
        if (response[this.TYPE_VISCOSITY] && Object.keys(response[this.TYPE_VISCOSITY]).length !== 0) {
          const visco = response[this.TYPE_VISCOSITY] as any;
          visco['yaxis'] = 'y2';
          curves.push(response[this.TYPE_VISCOSITY]);
        }
        if (response[this.TYPE_FOAMAT_VISCOSITY] && Object.keys(response[this.TYPE_FOAMAT_VISCOSITY]).length !== 0) {
          const visco = response[this.TYPE_FOAMAT_VISCOSITY] as any;
          visco['yaxis'] = 'y2';
          curves.push(response[this.TYPE_FOAMAT_VISCOSITY]);
        }
        return curves;
      })
    );
  }

  public getGeometryById(verison_uuid: string): Observable<Models.Meshing> {
    return this.httpclient.get<Models.Meshing>(this.PROJECT_VERSION + verison_uuid + '/geometry/?showall=1');
  }

  public getGeometries(uuid: string, showAll = false): Observable<Models.Meshing[]> {
    const query = showAll === true ? '?showAll=1' : '';
    return this.httpclient.get<Models.Meshing[]>(this.PROJECT + uuid + '/geometries/' + query);
  }

  public getMeshingVersionToken(uuid: string, accessToken: string): Observable<object> {
    const data = { modelType: 'stl' };
    return this.httpclient.put(this.PROJECT_VERSION + uuid + '/token/', data, this.httpOptions.json).pipe(
      map((response: any) => {
        const tokenId = response['id'];
        return {
          access_token: accessToken,
          modelKey: tokenId,
        };
      })
    );
  }

  public getMeshingVersionStl(uuid: string): Observable<object> {
    return this.httpclient.get(this.PROJECT_VERSION + uuid + '/stl/', this.httpOptions.json).pipe(
      map((response: any) => {
        const tokenId = response['id'];
        return {
          modelKey: tokenId,
        };
      })
    );
  }

  public getAppTypes(): Observable<any> {
    return this.httpclient.get<Models.Meshing[]>(this.PROJECT + 'appTypes/').pipe(
      map((result) => {
        const dictArr: any[] = [];

        for (const [key, value] of Object.entries(result)) {
          dictArr.push({ key: key, value: value });
        }

        return dictArr;
      })
    );
  }

  public addFoaming(project_uuid: string, version_uuid: string): Observable<Models.Meshing> {
    const obj = { project: project_uuid, mesh: version_uuid };
    return this.httpclient.post<Models.Meshing>(this.PROJECT_VERSION, obj);
  }

  public getFoamingProjectCompareCurves(uuids: string, type: string): Observable<any> {
    return this.httpclient.get(this.PROJECT_VERSION + 'plotlyCompare/?ids=' + uuids + '&plotType=' + type);
  }

  public getCompareVersions(uuids: string, type: string): Observable<any> {
    return this.httpclient.get(this.PROJECT_VERSION + 'compareVersions/?ids=' + uuids + '&plotType=' + type);
  }

  public getFoamingProjectMultiToken(uuids: string, accessToken: string): Observable<any> {
    return this.httpclient.put<any[]>(this.PROJECT_VERSION + 'tokens/?uuids=' + uuids, this.httpOptions.json).pipe(
      map((response) => {
        const tokens = [] as any;

        response.forEach((item) => {
          const tokenId = item['id'];
          const tokenDict = {
            access_token: accessToken,
            modelKey: tokenId,
          };
          tokens.push(tokenDict);
        });

        return tokens;
      })
    );
  }

  public retryVersion(uuid: string): Observable<object> {
    return this.httpclient.patch(this.PROJECT_VERSION + uuid + '/retry/?force=' + true, null);
  }

  public refreshResults(uuid: string): Observable<object> {
    return this.httpclient.patch(this.PROJECT_VERSION + uuid + '/refresh/', null);
  }

  public addFoamingVersion(Foaming: Models.FoamingObj, version_uuid: string): Observable<object> {
    return this.httpclient.patch(this.PROJECT_VERSION + version_uuid + '/', Foaming, this.httpOptions.json);
  }

  public addMeshingObject(meshing: any): Observable<Models.Meshing> {
    for (const [key, value] of Object.entries(meshing)) {
      if (Array.isArray(value)) {
        value.forEach((element, index) => {
          for (const key2 in element) {
            const innerDict = element[key2];
            if (typeof innerDict === 'object' && 'value' in innerDict) {
              meshing[key][index][key2] = element[key2].value;
            }
          }
        });
      } else {
        if (meshing.hasOwnProperty(key)) {
          const innerDict = meshing[key];
          if (typeof innerDict === 'object' && 'value' in innerDict) {
            meshing[key] = meshing[key].value;
          }
        }
      }

      if (meshing.hasOwnProperty(key)) {
        const innerDict = meshing[key];
        if (typeof innerDict === 'object' && 'value' in innerDict) {
          meshing[key] = meshing[key].value;
        }
      }
    }

    const formData = new FormData();
    formData.append('appType', meshing.appType);
    formData.append('modelKey', meshing.modelKey);
    formData.append('owner', meshing.owner || '');
    formData.append('title', meshing.title);
    formData.append('project', meshing.project || '');

    if (meshing.appType === 'pufoam_closed' || meshing.appType === 'pufoam_open') {
      formData.append('hCoarse', meshing.hCoarse);
      formData.append('gravityX', meshing.gravityX);
      formData.append('gravityY', meshing.gravityY);
      formData.append('gravityZ', meshing.gravityZ);
      formData.append('maxSurfaceRefinementLevel', String(meshing.maxSurfaceRefinementLevel));

      formData.append('stlFile', meshing.stlFile);
    }
    // if (meshing.hCoarse) {
    //   formData.append('hCoarse', String(meshing.meshSize / 1000));
    // }
    // if(meshing.gravityX) {
    //   formData.append('gravityX', meshing.gravityX);
    // }
    // if (meshing.maxSurfaceRefinementLevel) {
    //   formData.append('maxSurfaceRefinementLevel', String(meshing.refinementLevel));
    //   console.log('set max surface lvl');
    // }

    if (meshing.appType === 'porousBox') {
      for (const [key, value] of Object.entries(meshing)) {
        formData.append(key, value as any);
      }
    }

    return this.httpclient.post<Models.Meshing>(this.PROJECT_VERSION, formData);
  }

  public getMeshingDefaultSettings(apptype: string): Observable<Models.MeshStats> {
    return this.httpclient.get<Models.MeshStats>(this.PROJECT + 'defaults/?appType=' + apptype);
  }

  public getTemplatePreview(type: string): Observable<object> {
    return this.httpclient.get(this.PROJECT + 'templatePreview/?apptype=' + type, { responseType: 'blob' });
  }

  public getTemplatePreviewByFileType(type: string, fileType: string): Observable<object> {
    return this.httpclient.get(this.PROJECT + 'templatePreview/?apptype=' + type + '&filetype=' + fileType, {
      responseType: 'blob',
    });
  }

  public addFoamingMesh(meshing: Models.Meshing): Observable<Models.Meshing> {
    const formData = new FormData();
    // formData.append('appType', meshing.appType);
    formData.append('project', meshing.project || '');
    formData.append('mesh', meshing.mesh || '');
    return this.httpclient.post<Models.Meshing>(this.PROJECT_VERSION, formData);
  }

  public deleteMesh(uuid: string): Observable<object> {
    return this.httpclient.delete(this.PROJECT_VERSION + uuid + '/');
  }

  // end

  // mapper

  public getMasterModelToken(projectId: string, mastermodel: string): Observable<Models.MasterModelToken> {
    const object = JSON.stringify({ duration: 60, tokens: 1, mastermodel: mastermodel });
    return this.httpclient.put<Models.MasterModelToken>(this.PROJECT + projectId + '/modeltoken/', object, this.httpOptions['json']);
  }

  public getPartpd(partpdId: string): Observable<any> {
    return this.httpclient.get(this.PART_PD + partpdId + '/', this.httpOptions['json']);
  }

  public getMasterModels(projectId: string): Observable<Models.MasterModel[]> {
    return this.httpclient.get<Models.MasterModel[]>(this.PROJECT + projectId + '/models/', this.httpOptions['json']);
  }

  public deleteMasterModel(masterModelId: string): Observable<any> {
    return this.httpclient.delete(this.MASTER_MODEL + masterModelId + '/', this.httpOptions.json);
  }

  public getMappingToken(projectId: string): Observable<Models.Mapped> {
    const object = JSON.stringify({ duration: 60, tokens: 2 });
    return this.httpclient.put<Models.Mapped>(this.PROJECT_VERSION + projectId + '/token/', object, this.httpOptions['json']);
  }

  public getUsimdataMaterials(task: string, group: string, query: string, pageSize = 15, page = 1): Observable<any> {
    return this.httpclient.get(
      this.USIM_DATA + '?mapping=1&task=' + task + '&group=' + group + '&search=' + query + '&page_size=' + pageSize + '&page=' + page,
      this.httpOptions['json']
    );
  }

  public uploadMasterFile(projectuuid: string, files: any): Observable<any> {
    const formData = new FormData();

    formData.append('name', files['name']);

    if (files['modelfile']) {
      formData.append('modelfile', files['modelfile']);
    }

    if (files['includefiles']) {
      formData.append('includefiles', files['includefiles']);
    }

    return this.httpclient
      .post(this.PROJECT + projectuuid + '/models/', formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                const progress = Math.round((100 * event.loaded) / event.total || 0);
                return progress;
              }

              return 0;

            case HttpEventType.Response:
              return event.body;
            default:
              return `Unhandled event: ${event.type}`;
          }
        })
      );
  }

  public loadURL(url: string) {
    return this.httpclient.get(url, this.httpOptions['json']);
  }

  public partpd(projectId: string, pdType: string, moldedVersionId?: number, coord?: any, moldFlowFiles?: any, transformtype?: string): Observable<any> {
    const formData = new FormData();
    formData.append('pdType', pdType);
    formData.append('transformtype', transformtype || '');

    if (pdType == 'isofill') {
      return this.httpclient.post(this.PROJECT + projectId + '/processdata/', formData);
    } else if (moldedVersionId) {
      formData.append('molded_version', moldedVersionId.toString());
      return this.httpclient.post(this.PROJECT + projectId + '/processdata/', formData);
    } else if (coord) {
      formData.append('x', coord['x']);
      formData.append('y', coord['y']);
      formData.append('z', coord['z']);
      return this.httpclient.post(this.PROJECT + projectId + '/processdata/', formData);
    } else if (moldFlowFiles && pdType === 'moldflow') {
      formData.append('partFile ', moldFlowFiles['patFile']);
      formData.append('oriFile  ', moldFlowFiles['xmlFile']);
    } else if (moldFlowFiles && pdType === 'moldex') {
      formData.append('partFile ', moldFlowFiles['inpFile']);
      formData.append('oriFile  ', moldFlowFiles['o2dFile']);
    }

    return this.httpclient
      .post(this.PROJECT + projectId + '/processdata/', formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                const progress = Math.round((100 * event.loaded) / event.total);
                return { status: 'progress', message: progress };
              }

              return 0;

            case HttpEventType.Response:
              return event.body;
            default:
              return `Unhandled event: ${event.type}`;
          }
        })
      );
  }

  // // job and fib files
  public getFile(mappingVersionId: string, downloadFile: string, projectName: string) {
    return this.httpclient
      .get(this.PROJECT_VERSION + mappingVersionId + '/' + downloadFile, {
        reportProgress: true,
        observe: 'events',
        responseType: 'arraybuffer',
      })
      .pipe(
        map((event) => {
          console.log('event');
          console.log(event);
          switch (event.type) {
            case HttpEventType.DownloadProgress:
              if (event.total) {
                const progress = Math.round((100 * event.loaded) / event.total);
                return { status: 'progress', message: progress };
              }
              return 0;

            case HttpEventType.Response:
              let data: any;
              if (event.type === HttpEventType.Response) {
                data = event.body ? event.body : 'file';
              }

              const blob = new Blob([data]);
              let fileName = `${projectName}.zip`;
              // if(downloadFile == 'jobFile') {
              //   fileName = `${projectName}-version-${verisonNumber}.inp`;
              // }
              saveAs(blob, fileName);
              return event.body;

            case HttpEventType.ResponseHeader:
              return event.status;
            default:
              return `Unhandled event: ${event.type}`;
          }
        })
      );
  }

  public getMappingPreview(uuid: string): Observable<any> {
    return this.httpclient.get<Blob>(this.PROJECT + uuid + '/preview', this.httpOptions.arrayblob);
  }

  public getMasterModelPreview(uuid: string): Observable<any> {
    return this.httpclient.get<Blob>(this.MASTER_MODEL + uuid + '/preview', this.httpOptions.arrayblob);
  }

  public getVersionPreview(uuid: string): Observable<any> {
    return this.httpclient.get<Blob>(this.PROJECT_VERSION + uuid + '/preview', this.httpOptions.arrayblob);
  }

  public getUsimdataPlot(materialSlug: string): Observable<any> {
    return this.httpclient.get(this.USIM_DATA + materialSlug + '/plot/', this.httpOptions['json']);
  }

  // end

  // sinter

  public getVersionStl(uuid: string, name: string): Observable<any> {
    return this.httpclient.get<any>(this.PROJECT_VERSION + uuid + '/stl/').pipe(
      map((result) => {
        const blob = new Blob([result], {});

        // Helper.DownloadFile.createDownLoadFile(blob, '.stl', name);

        return result;
      })
    );
  }

  public createProjectVersion(object: any): Observable<object> {
    return this.httpclient.post(this.PROJECT_VERSION, object, this.httpOptions.json);
  }
  public updateProjectVersion(object: any): Observable<object> {
    return this.httpclient.patch(this.PROJECT_VERSION, object, this.httpOptions.json);
  }

  public getVersionToken(uuid: string, access_token: string): Observable<any> {
    const obj = { duration: 60, tokens: 1 };
    return this.httpclient.put<any>(this.PROJECT_VERSION + uuid + '/token/', obj, this.httpOptions.json).pipe(
      map((response: any) => {
        const tokenId = response['id'];
        const tokenDict = { 'access_token': access_token, 'modelKey': tokenId };
        return tokenDict;
      })
    );
  }

  public addMasterModel(modelfile: File, uuid: string): Observable<Models.MasterModel> {
    const formData = new FormData();

    console.log('model');
    console.log(modelfile);

    formData.append('modelfile', modelfile);
    return this.httpclient.post<Models.MasterModel>(this.PROJECT + uuid + '/models/', formData);
  }

  // end

  // stonechip

  public createStoneChipSim(data: object): Observable<any> {
    return this.httpclient.post<any>(this.STONE_CHIP, data, this.httpOptions['json']);
  }

  public getStoneChipMaterials(): Observable<any> {
    return this.httpclient.get<any>(this.STONE_CHIP + 'materials/');
  }

  public getStoneChipToken(id: string): Observable<object> {
    const object = JSON.stringify({ duration: 60, tokens: 1 });
    return this.httpclient.put(this.STONE_CHIP + id + '/token/', object, this.httpOptions['json']);
  }

  // end

  // molded

  // getProject(projectId: number): Observable<any> {
  //   const url = this.projectsUrl + projectId + '/';
  //   return this.http.get<Projects[]>(url);
  // }

  public newProject(data: any): Observable<any> {
    const options = {
      headers: new HttpHeaders({}),
      reportProgress: true,
    };
    const req = new HttpRequest('POST', this.PROJECT, data, options);
    return this.httpclient.request(req);
  }

  // getProjectById(uuid: string, params?: string): Observable<any> {
  //   let url = `${this.projectsUrl}${uuid}/`;
  //   if (params) url += `?${params}`;
  //   return this.http.get<Projects[]>(url);
  // }

  public getReportById(uuid: string): Observable<any> {
    return this.httpclient.get<any>(this.PROJECT_VERSION + uuid + '/report/');
  }

  public checkVersionStatus(versionId: string): Observable<any> {
    return this.httpclient.get<any>(this.PROJECT_VERSION + versionId + '/status/');
  }

  // quickmesh file upload
  public uploadFile(data: any): Observable<any> {
    return this.httpclient.post(this.PROJECT, data, this.httpOptions['json']);
  }

  public loadImage(modelId: number) {
    return this.httpclient.get(this.PROJECT_VERSION + modelId + '/preview/', {
      responseType: 'blob',
    });
  }

  public cloneVersion(projectId: string, data: object) {
    return this.httpclient.put<any>(this.PROJECT + projectId + '/clone-version/', data);
  }

  // public removeProjectVersion(projectVersionId: string): Observable<any> {
  //   const url = `${this.projectVersionsUrl}${projectVersionId}/`;
  //   return this.httpclient.delete<any>(url);
  // }

  public getMaterials(filters?: string): Observable<Models.Page> {
    if (filters) {
      filters = filters.replace('+', '%2B');
      return this.httpclient.get<Models.Page>(this.HOST + '/api/materialinfo/?' + filters, this.httpOptions.json).pipe(
        map((page) => {
          return page;
        })
      );
    } else {
      return this.httpclient.get<Models.Page>(this.HOST + '/api/materialinfo/?generic=false', this.httpOptions.json).pipe(
        map((page: any) => {
          return page;
        })
      );
    }
  }

  public getMaterialsByUrl(url: string, filters?: string): Observable<Models.Page> {
    if (filters) {
      filters = filters.replace('+', '%2B');
      return this.httpclient.get<Models.Page>(this.HOST + '/api/materialinfo/?' + filters);
    }
    return this.httpclient.get<Models.Page>(url);
  }

  public getResult(uuid: string): Observable<any> {
    return this.httpclient
      .get(this.PROJECT_VERSION + uuid + '/results', {
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .pipe(
        map((results) => {
          const content_disposition = results.headers.get('content-disposition');
          if (content_disposition) {
            const data = {
              arrayBuffer: results.body,
              fileType: content_disposition.substring(content_disposition.lastIndexOf('.') + 1),
            };

            return data;
          }
          return null;
        })
      );
  }
  // end

  public getConsent(): Observable<any> {
    return this.httpclient.get(this.CONSENT, this.httpOptions.json);
  }

  public postConsent(permit: Models.CONSET_STATUS): Observable<any> {
    const data = { status: permit };
    return this.httpclient.post(this.CONSENT, data, this.httpOptions.json);
  }
}
